import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import EnquiryForm from '../components/EnquiryForm';
import CountryCard from '../components/CountryCard';
import FaqItem from '../components/FaqItem';
import RevealProvider from '../components/RevealProvider';
import { getCountry, COUNTRIES, formatUSD } from '../data/countries';

const SAFE_HTML = { ALLOWED_TAGS: ['em', 'strong', 'br', 'span'], ALLOWED_ATTR: [] };
const bold = (text) => ({ __html: DOMPurify.sanitize((text ?? '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'), SAFE_HTML) });

const NATIONALITIES = [
  { code: 'in', label: '🇮🇳 India' },
  { code: 'ng', label: '🇳🇬 Nigeria' },
  { code: 'ph', label: '🇵🇭 Philippines' },
  { code: 'ae', label: '🇦🇪 UAE' },
  { code: 'za', label: '🇿🇦 South Africa' },
  { code: 'us', label: '🇺🇸 United States' },
  { code: 'gb', label: '🇬🇧 United Kingdom' },
  { code: 'ca', label: '🇨🇦 Canada' },
  { code: 'br', label: '🇧🇷 Brazil' },
  { code: 'other', label: 'Other' },
];

// Generic per-nationality guidance, lightly tailored by country context.
function buildNationalityContent(country, code){
  const C = country.name;
  const table = {
    in: { heading: `🇮🇳 Applying from India`, bullets: [
      `**Police Clearance Certificate (PCC)** must come from your current state’s Passport Seva Kendra and be apostilled by MEA — allow 3–4 weeks.`,
      `**Bank statements in INR** are accepted; the consulate converts at current ECB rates. Your rupee income must clear the ${C} threshold at the weakest recent exchange rate.`,
      `**Prior visa history** — any prior Schengen or ${C}-region rejection must be disclosed. It doesn’t disqualify you, but the case needs careful preparation.`,
    ], caveat: `Nomad Compass has filed visa applications for Indian clients from Mumbai, Bengaluru, Delhi, and Hyderabad.` },
    ng: { heading: `🇳🇬 Applying from Nigeria`, bullets: [
      `**Proof of income** must be in USD/EUR equivalents. Naira bank statements are accepted but supplemented with USD-denominated evidence (Upwork, employer pay stubs, Wise).`,
      `**NBI clearance** is required, apostilled via the Federal Ministry of Foreign Affairs in Abuja. Allow 4 weeks.`,
      `**Consular interview** at Lagos or Abuja is more detailed. Expect questions about your source of income and ties to Nigeria — standard, not a red flag.`,
    ], caveat: `We prepare interview briefings specifically for Nigerian applicants.` },
    ph: { heading: `🇵🇭 Applying from the Philippines`, bullets: [
      `**NBI clearance** must be obtained via the NBI Clearance Center, with Apostille from the DFA. Start this in Week 1 — processing is 10–14 working days.`,
      `**Proof of income** — if you’re already remote-employed abroad, provide your employer contract plus 6 months of bank deposits showing salary crediting.`,
      `**Consular interview** at the ${C} consulate in Manila is generally straightforward; standard family and financial questions.`,
    ], caveat: `We work with a partner in Manila for document legalisation and NBI processing.` },
    ae: { heading: `🇦🇪 Applying from the UAE`, bullets: [
      `**Expats on UAE residency** file via home-country documents (criminal record from your passport country, not UAE). UAE bank statements and income are accepted.`,
      `**UAE-issued documents** (like your employment contract) are generally accepted without apostille if in English or Arabic.`,
      `**Dubai consulate** appointments for ${C} are typically available within 2–4 weeks.`,
    ], caveat: `Dubai consulate processing is among the most efficient globally.` },
    za: { heading: `🇿🇦 Applying from South Africa`, bullets: [
      `**SAPS police clearance** takes 6–8 weeks in most provinces — the critical path item. Start it Week 1.`,
      `**Apostille** issued by DIRCO in Pretoria, typically 5–10 working days.`,
      `**Consular interview** for ${C} is thorough but approval rates are high for properly prepared applications.`,
    ], caveat: `South African clients often find favourable tax treatment in ${C}.` },
    us: { heading: `🇺🇸 Applying from the United States`, bullets: [
      `**FBI background check** (not state-level) is required, apostilled by the US Department of State. Total time: 2–4 weeks via a channeler like IdentoGO.`,
      `**Consulates** for ${C} vary by state. Book your slot in Week 1 — popular consulates run 6–8 weeks out in peak periods.`,
      `**Tax considerations** matter for Americans: because of US citizenship-based taxation, you’ll continue to file US taxes alongside ${C}’s. We introduce you to a cross-border tax specialist at no charge.`,
    ], caveat: `Americans make up a large share of our ${C} client base.` },
    gb: { heading: `🇬🇧 Applying from the United Kingdom`, bullets: [
      `**ACRO police certificate** (standard, not enhanced) is accepted, apostilled by the UK FCDO Legalisation Office. Allow 10–15 working days.`,
      `**Consulates** for ${C} in London are typically 4–8 weeks out for appointments.`,
      `**Accommodation proof** — UK applicants commonly use a 12-month rental or a short-term booking with a reserved longer lease. Both work if properly structured.`,
    ], caveat: `UK clients are one of our largest nationality groups.` },
    ca: { heading: `🇨🇦 Applying from Canada`, bullets: [
      `**RCMP criminal record check** with fingerprinting is required. Allow 3–5 weeks for processing and apostille via Global Affairs Canada.`,
      `**Quebec residents** may need additional documents translated to French or the local language of ${C}.`,
      `**Consular appointments** in Toronto and Montreal for ${C} are currently 4–6 weeks out.`,
    ], caveat: `We coordinate RCMP background checks on your behalf.` },
    br: { heading: `🇧🇷 Applying from Brazil`, bullets: [
      `**Federal Police Criminal Record** is obtained online via the Federal Police website, then apostilled by Itamaraty. Process is 7–14 days.`,
      `**${C === 'Portugal' ? 'CPLP Agreement' : 'Language considerations'}** — ${C === 'Portugal' ? 'Brazilians benefit from expedited Portuguese residency via CPLP; we’ll tell you if it’s a better path than the D8.' : 'Translations into the local language may be needed; we coordinate sworn translators.'}`,
      `**Consulates** in S\u00E3o Paulo, Rio, Brasília all handle ${C} applications efficiently — typically 3–5 week wait times.`,
    ], caveat: `Brazilian clients often qualify for favourable treatment in ${C}.` },
    other: { heading: `🌍 Applying from another country?`, bullets: [
      `We’ve filed ${C} applications for clients from 30+ different nationalities. Process varies meaningfully by passport.`,
      `We maintain active country-specific playbooks across Europe, MENA, Asia-Pacific, and Latin America.`,
      `Tell us your passport in the enquiry form and we’ll share a nationality-specific briefing before your first call — free, no obligation.`,
    ], caveat: `We add a new country-specific guide every 4–6 weeks based on client demand.` },
  };
  return table[code] || table.other;
}

function buildTimeline(country){
  const total = Math.max(4, country.processingWeeks);
  const bookingWeek = Math.min(2, Math.floor(total * 0.25));
  const interviewWeek = Math.max(bookingWeek + 2, Math.floor(total * 0.5));
  const reviewStart = interviewWeek + 1;
  const issueWeek = Math.max(reviewStart + 1, total - 2);
  const arrivalWeek = total;
  return [
    { week: 'Week 1', title: 'Kickoff call & document plan', body: `A free 30-minute call. We audit your profile, confirm ${country.name} is the right fit for your situation, and hand you a personalised document checklist and timeline.` },
    { week: 'Week 2–3', title: 'Document gathering', body: `Criminal record check from your home country, proof of income (6 months bank statements, employment or freelance contracts), proof of accommodation.`, caution: `Where most DIY applicants stall: sourcing the criminal record takes 3–5 weeks if you don’t know the right channel.` },
    { week: `Week ${Math.max(2, bookingWeek + 2)}`, title: 'Apostille & translation', body: `All non-${country.languages?.[0] || 'local-language'} documents translated by a sworn translator accepted by ${country.name}’s authorities, and apostilled via your home country’s competent authority.` },
    { week: `Week ${Math.max(bookingWeek + 3, 5)}`, title: 'Consulate appointment booked', body: `${country.name} consulate slots are typically 4–8 weeks out in peak periods. We book yours in Week 1 and confirm now.`, milestone: true, caution: `If the slot wasn’t booked early, the whole timeline slips by 4–6 weeks.` },
    { week: `Week ${interviewWeek}–${interviewWeek + 1}`, title: 'Interview & submission', body: `Attend your consulate in person. Submit the complete application pack. Biometrics captured. You leave with a submission receipt and tracking reference.` },
    { week: `Week ${reviewStart}–${issueWeek}`, title: 'Consular review period', body: `${country.name} authorities review your application. We respond on your behalf if the consulate requests clarifications — so nothing slips.` },
    { week: `Week ${issueWeek + 1}`, title: `${country.visaName} issued`, body: `Entry visa stamped in your passport. You can now fly to ${country.name}. Valid for 4–6 months — your window to enter and attend any on-arrival appointment.`, milestone: true },
    { week: `Week ${arrivalWeek}+`, title: `Arrival & residence card`, body: `Attend any required on-arrival residency/registration appointment. Biometrics captured. You receive your residence card within 2–4 weeks.` },
  ];
}

function buildFaqs(country){
  const C = country.name;
  return [
    { q: `Can I bring my spouse and children on the ${country.visaName}?`, a: `Yes. ${C}’s programme allows family reunification. Your spouse and dependent children can be added to your application or apply separately after you’re approved. The income requirement rises to ${formatUSD(country.incomeSpouse)}/mo for a couple. We handle all family dependent applications as part of our flat fee — we don’t charge per person.` },
    { q: `Do I have to move to ${C} immediately after approval?`, a: `Your entry visa is valid for 4–6 months from issue. You must enter ${C} within that window and attend any on-arrival appointment within the first few months. Once resident, most programmes require 6+ months per year physically in the country.` },
    { q: `What are my tax obligations if I get the visa?`, a: `${country.taxFriendly ? `${C} offers favourable tax treatment on foreign income during the programme.` : `Once you become a ${C} tax resident, worldwide income becomes taxable.`} We connect you with a ${C}-specific tax advisor as part of the service so your structure is clean before arrival.` },
    { q: `Does remote work for a foreign employer count as income?`, a: `Yes — as long as the work is performed for clients or employers based outside ${C}. W-2/ICE income, 1099/self-employment, or income from a business you own abroad all qualify. You’ll need 6 months of bank statements showing consistent deposits at or above ${formatUSD(country.minIncome)}/mo.` },
    { q: `What are common reasons applications get rejected?`, a: `The top three: (1) Inconsistent income — applicants whose 6-month average just barely clears the threshold, with large month-to-month variance. (2) Inadequate accommodation proof — a hotel booking instead of a real lease. (3) Translation errors — documents translated by non-sworn translators, or missed apostilles. Most rejections are preventable with proper preparation.` },
    { q: `Is your $500 fee refundable if I’m rejected?`, a: `We only take on applications we believe we can win. During the initial consultation, we review your profile honestly — if we think your application is borderline or unlikely to succeed, we’ll tell you before you pay anything. In the rare event of a rejection on a case we accepted, we’ll resubmit or refile at no additional service fee.` },
  ];
}

export default function CountryDetail(){
  const { slug } = useParams();
  const country = getCountry(slug);
  const navigate = useNavigate();
  const [natCode, setNatCode] = useState('in');

  if (!country){
    return (
      <div className="notfound">
        <Helmet><title>Country not found — Nomad Compass</title></Helmet>
        <div>
          <h1><em>Not here.</em></h1>
          <p>We don’t have a page for that country yet. See all the programmes we file for below.</p>
          <button className="btn-primary" onClick={() => navigate('/countries')}>View all countries</button>
        </div>
      </div>
    );
  }

  const nat = buildNationalityContent(country, natCode);
  const timeline = buildTimeline(country);
  const faqs = buildFaqs(country);
  const elsewhere = COUNTRIES.filter(c => c.region === country.region && c.slug !== country.slug).slice(0, 4);
  if (elsewhere.length < 4) elsewhere.push(...COUNTRIES.filter(c => c.region !== country.region && !elsewhere.includes(c)).slice(0, 4 - elsewhere.length));

  const pageTitle = `${country.name} Digital Nomad Visa — Requirements, Cost, Timeline | Nomad Compass`;
  const pageDesc = `${country.visaName} requirements, real all-in cost, week-by-week timeline, and nationality-specific guidance. Done-for-you ${country.name} visa assistance from $500.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "GovernmentService",
    "name": country.visaName,
    "serviceType": "Digital Nomad Visa",
    "provider": { "@type": "GovernmentOrganization", "name": `Government of ${country.name}` },
    "areaServed": { "@type": "Country", "name": country.name },
    "url": `https://nomadcompasshub.com/countries/${country.slug}`,
    "termsOfService": country.govSite,
    "offers": { "@type": "Offer", "priceCurrency": country.currency, "description": `Income requirement: ${formatUSD(country.minIncome)}/month. Validity: ${country.validity}. Processing: ${country.processing}.` }
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://nomadcompasshub.com/" },
      { "@type": "ListItem", "position": 2, "name": "Countries", "item": "https://nomadcompasshub.com/countries" },
      { "@type": "ListItem", "position": 3, "name": country.name, "item": `https://nomadcompasshub.com/countries/${country.slug}` },
    ]
  };

  return (
    <RevealProvider>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://nomadcompasshub.com/countries/${country.slug}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={`https://nomadcompasshub.com/countries/${country.slug}`} />
        <meta property="og:image" content={country.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <TopBar variant="page" breadcrumb={country.name} />

      <section className="cp-hero">
        <div className="cp-hero-top"><span className="cp-flag" aria-hidden="true">{country.flag}</span>{country.name} · {country.visaName}</div>
        <h1 className="cp-hero-title">The {country.name} {country.visaName.includes(country.name) ? country.visaName.replace(country.name, '').trim() : country.visaName}, <em>clearly explained</em>—then handled for you.</h1>
        <p className="cp-hero-lead">Everything you need to know about {country.name}’s programme: the real income threshold, the full all-in cost (not just the government fee), the week-by-week timeline, and what changes if you’re applying from India, Nigeria, or anywhere beyond the typical US/UK path. If you’d rather not do this yourself — we file it for you.</p>

        <div className="data-card reveal">
          <div className="data-cell">
            <div className="data-label">Income requirement</div>
            <div className="data-val">{formatUSD(country.minIncome)}<em>/mo</em></div>
            <div className="data-note">Applicant alone</div>
          </div>
          <div className="data-cell">
            <div className="data-label">Processing time</div>
            <div className="data-val">{country.processing}</div>
            <div className="data-note">Consulate submission to approval</div>
          </div>
          <div className="data-cell">
            <div className="data-label">Visa validity</div>
            <div className="data-val">{country.validity}</div>
            <div className="data-note">{country.renewable ? 'Renewable' : 'Non-renewable'}</div>
          </div>
          <div className="data-cell">
            <div className="data-label">Path to residency</div>
            <div className="data-val">{country.region === 'europe' ? '5–10 yrs' : country.region === 'middle_east' ? 'Long-term' : '3–5 yrs'}</div>
            <div className="data-note">Typical renewal trajectory</div>
          </div>
        </div>

        <div className="cp-hero-actions">
          <a href="#enquiry" className="btn-primary" data-testid="cta-apply">Start my {country.name} application</a>
          <div className="cp-jump">or jump to: <a href="#timeline">week-by-week timeline</a> · <a href="#cost">real cost breakdown</a></div>
        </div>
      </section>

      <section className="moat-nat" id="nationality">
        <div className="moat-inner">
          <div className="reveal">
            <div className="moat-eyebrow">By your nationality <span className="pill">Interactive</span></div>
            <h2 className="moat-title">What most guides <em>forget to tell you.</em></h2>
            <p className="moat-sub">Every article online writes for the default US/UK applicant. But documents, costs, and real rejection risks vary enormously by passport. Select yours to see what {country.name}’s process actually looks like for you.</p>
          </div>
          <div className="nat-picker reveal">
            {NATIONALITIES.map(n => (
              <button key={n.code} onClick={() => setNatCode(n.code)} className={`nat-chip ${natCode === n.code ? 'active' : ''}`} data-testid={`nat-${n.code}`}>{n.label}</button>
            ))}
          </div>
          <div className="nat-panel reveal" data-testid="nat-panel">
            <div className="nat-heading">{nat.heading}</div>
            <div className="nat-body">
              <p>{country.name} applicants from this nationality face a specific set of requirements and timelines.</p>
              <ul>
                {nat.bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={bold(b)} />)}
              </ul>
              <div className="nat-caveat">{nat.caveat}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="moat-cost" id="cost">
        <div className="cost-grid">
          <div className="cost-left reveal">
            <div className="moat-eyebrow">The real cost</div>
            <h2 className="moat-title">Not just the government fee. <em>The full number.</em></h2>
            <p className="cost-intro">Every competitor quotes only the headline government visa fee. That’s misleading — the true all-in cost to get from signed application to approved residency for {country.name} is typically far higher. Here’s the honest breakdown, including our service fee.</p>
            <div className="cost-note">
              <strong>Why honesty matters here</strong>
              A botched DIY application wastes every line item in this table — not just the government fee. Our fee is a small fraction of what’s at stake.
            </div>
          </div>
          <div className="cost-table reveal">
            <div className="cost-row"><div className="cost-item"><strong>Government visa application fee</strong><span>Paid to {country.name} consulate</span></div><div className="cost-amount">$75–$200</div></div>
            <div className="cost-row"><div className="cost-item"><strong>Residence card / on-arrival registration</strong><span>Paid after arrival in {country.name}</span></div><div className="cost-amount">$100–$300</div></div>
            <div className="cost-row"><div className="cost-item"><strong>Certified document translations</strong><span>Birth cert., criminal record, bank statements</span></div><div className="cost-amount">$180–$400</div></div>
            <div className="cost-row"><div className="cost-item"><strong>Apostille / legalization of documents</strong><span>Varies by country of issue</span></div><div className="cost-amount">$120–$250</div></div>
            <div className="cost-row"><div className="cost-item"><strong>Health insurance (required, 1 yr min.)</strong><span>{country.name}-valid private health cover</span></div><div className="cost-amount">$450–$900</div></div>
            <div className="cost-row"><div className="cost-item"><strong>Tax ID + local bank account</strong><span>Setup via local representative where required</span></div><div className="cost-amount">$100–$300</div></div>
            <div className="cost-row"><div className="cost-item"><strong>Criminal record check (home country)</strong><span>Varies by jurisdiction</span></div><div className="cost-amount">$30–$150</div></div>
            <div className="cost-total">
              <strong>Government &amp; filing costs (estimate)</strong>
              <div className="cost-total-val">$1,150–$2,500</div>
              <div className="cost-total-sub">Range depends on your country of origin and family size. Exact quote on enquiry.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="moat-timeline" id="timeline">
        <div className="timeline-head reveal">
          <div className="moat-eyebrow">Week-by-week reality</div>
          <h2 className="moat-title">Not just “{country.processing}.” <em>Here’s what each week looks like.</em></h2>
          <p className="moat-sub">Every guide quotes a vague range. We map the actual work: what you do, what the consulate does, and where DIY applicants typically stall.</p>
        </div>
        <div className="timeline">
          {timeline.map((t, i) => (
            <div key={i} className="tline-row reveal">
              {i % 2 === 0 ? (
                <>
                  <div className={`tline-card ${t.milestone ? 'milestone' : ''}`}>
                    <div className="tline-title">{t.title}</div>
                    <div className="tline-body">{t.body}</div>
                    {t.caution && <div className="tline-caution">{t.caution}</div>}
                  </div>
                  <div className="tline-week">{t.week}</div>
                  <div className="tline-empty"></div>
                </>
              ) : (
                <>
                  <div className="tline-empty"></div>
                  <div className="tline-week">{t.week}</div>
                  <div className={`tline-card ${t.milestone ? 'milestone' : ''}`}>
                    <div className="tline-title">{t.title}</div>
                    <div className="tline-body">{t.body}</div>
                    {t.caution && <div className="tline-caution">{t.caution}</div>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="requirements">
        <div className="req-grid">
          <div className="req-col reveal">
            <h3>Who qualifies</h3>
            <p>You’re eligible for {country.visaName} if you can demonstrate:</p>
            <div className="req-list">
              <div className="req-item"><div><strong>Remote income ≥ {formatUSD(country.minIncome)}/month</strong><span>Must be earned from outside {country.name} (employer, freelance, or business)</span></div></div>
              <div className="req-item"><div><strong>Valid passport, 6+ months remaining</strong><span>With at least 2 blank visa pages</span></div></div>
              <div className="req-item"><div><strong>Clean criminal record</strong><span>From every country you’ve lived in for 12+ months in the last 5 years</span></div></div>
              <div className="req-item"><div><strong>Health insurance valid in {country.name}</strong><span>Typical minimum $30,000+ coverage, 12-month policy</span></div></div>
              <div className="req-item"><div><strong>Proof of accommodation</strong><span>Rental contract or hotel booking for the initial period</span></div></div>
              <div className="req-item"><div><strong>Savings buffer</strong><span>Varies by country; typically 12× monthly threshold in bank statements</span></div></div>
            </div>
          </div>
          <div className="req-col reveal">
            <h3>Documents you’ll need</h3>
            <p>Every application requires these documents — translated and apostilled where required.</p>
            <div className="req-list">
              <div className="req-item"><div><strong>Passport + copies</strong><span>All used pages, plus current identity page</span></div></div>
              <div className="req-item"><div><strong>6 months of bank statements</strong><span>Proving consistent monthly income above threshold</span></div></div>
              <div className="req-item"><div><strong>Employment contract or client contracts</strong><span>Demonstrating remote work arrangement</span></div></div>
              <div className="req-item"><div><strong>Criminal record certificate (apostilled)</strong><span>Police clearance from home country</span></div></div>
              <div className="req-item"><div><strong>Local tax ID + bank statement</strong><span>Where required by the host country</span></div></div>
              <div className="req-item"><div><strong>Proof of accommodation in {country.name}</strong><span>12-month lease preferred; hotel booking minimum</span></div></div>
              <div className="req-item"><div><strong>Health insurance certificate</strong><span>{country.name}-valid, 12 months</span></div></div>
            </div>
          </div>
        </div>
      </section>

      <section className="place-section">
        <div className="place-grid">
          <div className="place-image reveal">
            <img src={country.image} alt={`${country.name} lifestyle`} />
          </div>
          <div className="place-text reveal">
            <div className="moat-eyebrow">Life in {country.name}</div>
            <h2>The numbers aside — <em>is it actually the life you want?</em></h2>
            <p>{country.blurb}</p>
            <p>For families, schooling and healthcare in {country.capital} are among the considerations we plan with you — not just the paperwork. The cost landscape, tax treatment, and visa renewal pathways all factor into whether {country.name} fits the life you’re trying to build.</p>
            <div className="place-stats">
              <div className="place-stat"><strong>{country.capital}</strong><span>Capital &amp; primary nomad hub</span></div>
              <div className="place-stat"><strong>{country.validity}</strong><span>Initial visa validity</span></div>
              <div className="place-stat"><strong>{country.processing}</strong><span>Typical end-to-end processing</span></div>
              <div className="place-stat"><strong>{country.taxFriendly ? 'Favourable' : 'Standard'}</strong><span>Tax treatment on foreign income</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="elsewhere">
        <div className="elsewhere-inner">
          <div className="elsewhere-head reveal">
            <h3>Considering <em>elsewhere?</em></h3>
            <Link to="/countries">View all 40 countries →</Link>
          </div>
          <div className="else-grid">
            {elsewhere.map(c => <CountryCard key={c.slug} country={c} size="small" />)}
          </div>
        </div>
      </section>

      <section className="faq-section-cp">
        <div className="faq-wrap">
          <div className="reveal" style={{ textAlign: 'center' }}>
            <div className="moat-eyebrow">Frequently asked</div>
            <h2 className="moat-title">Questions before <em>you enquire.</em></h2>
          </div>
          {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
        </div>
      </section>

      <section className="enq" id="enquiry">
        <div className="enq-grid">
          <div className="enq-left reveal">
            <div className="moat-eyebrow">Ready to start?</div>
            <h2>Tell us about <em>your situation.</em></h2>
            <p>Fill in a few details and we’ll reply within one working day with a realistic assessment — whether you qualify, what it’ll cost for your specific case, and how quickly we can move.</p>
            <p>The initial consultation is free and there’s no obligation. We’d rather say no honestly than take on a case we can’t win.</p>
            <div className="enq-promises">
              <div className="enq-promise">Your details are kept in complete confidence.</div>
              <div className="enq-promise">A named expert replies personally — no chatbots.</div>
              <div className="enq-promise">If {country.name} isn’t right for you, we’ll tell you what is.</div>
            </div>
          </div>
          <EnquiryForm pageSource={country.slug} variant="country" defaultCountry={country.name} />
        </div>
      </section>

      <Footer />
    </RevealProvider>
  );
}
